import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Shared hook for dynamic dropdown positioning in Shadow DOM.
 *
 * - Uses position: fixed relative to viewport (getBoundingClientRect)
 * - Recalculates on scroll, resize, and iOS visualViewport changes (keyboard)
 * - rAF-throttled to avoid layout thrashing
 * - Returns maxHeight to prevent dropdown from overflowing viewport
 * - Listens to 'dropdown-close-all' CustomEvent for external close triggers
 *
 * @param {boolean} isOpen - Whether the dropdown is currently open
 * @param {React.RefObject} triggerRef - Ref to the trigger element (button/input)
 * @param {React.RefObject} dropdownRef - Ref to the dropdown element (portal content)
 * @param {Object} options
 * @param {number} options.gap - Gap between trigger and dropdown (default: 4)
 * @param {number} options.defaultMaxHeight - Default max height (default: 230)
 * @param {Function} options.onCloseAll - Callback when dropdown-close-all fires
 * @returns {{ top: number, left: number, width: number, maxHeight: number }}
 */
export function useDropdownPosition(isOpen, triggerRef, dropdownRef, options = {}) {
  const { gap = 4, defaultMaxHeight = 230, onCloseAll } = options;
  const [position, setPosition] = useState({ top: 0, left: 0, right: 0, width: 0, maxHeight: defaultMaxHeight });
  const rafRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;

    // Available space below the trigger
    const spaceBelow = viewportHeight - rect.bottom - gap;

    // maxHeight: don't exceed available space, but also don't exceed default
    const maxHeight = Math.max(80, Math.min(defaultMaxHeight, spaceBelow - 4));

    setPosition({
      top: rect.bottom + gap,
      left: rect.left,
      right: window.innerWidth - rect.right,
      width: rect.width,
      maxHeight,
    });
  }, [triggerRef, gap, defaultMaxHeight]);

  // rAF-throttled update to avoid layout thrashing
  const scheduleUpdate = useCallback(() => {
    if (rafRef.current) return; // already scheduled
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updatePosition();
    });
  }, [updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    // Initial position calculation
    updatePosition();

    // Recalculate after DOM reflow (dropdown ref may be null on first render)
    const initialTimeout = setTimeout(updatePosition, 50);

    // Listen to scroll (capture: true catches scroll events inside Shadow DOM)
    window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    // iOS keyboard awareness via visualViewport API
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', scheduleUpdate);
      vv.addEventListener('scroll', scheduleUpdate);
    }

    return () => {
      clearTimeout(initialTimeout);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      window.removeEventListener('scroll', scheduleUpdate, { capture: true });
      window.removeEventListener('resize', scheduleUpdate);
      if (vv) {
        vv.removeEventListener('resize', scheduleUpdate);
        vv.removeEventListener('scroll', scheduleUpdate);
      }
    };
  }, [isOpen, updatePosition, scheduleUpdate]);

  // Listen to dropdown-close-all for external close triggers
  // (e.g., filter panel scrolling in CarsPage)
  useEffect(() => {
    if (!onCloseAll) return;

    const handleCloseAll = () => onCloseAll();
    window.addEventListener('dropdown-close-all', handleCloseAll);
    return () => window.removeEventListener('dropdown-close-all', handleCloseAll);
  }, [onCloseAll]);

  return position;
}
